import { forwardRef, useMemo, VideoHTMLAttributes } from 'react';

import { FileFragment } from '../graphql/generated/graphql';
import { getVideoSources } from '../utils';

interface Props extends VideoHTMLAttributes<HTMLVideoElement> {
  file: FileFragment;
  className?: string;
}

export const Video = forwardRef<HTMLVideoElement, Props>(
  (
    {
      file,
      className,
      loop = true,
      muted = true,
      playsInline = true,
      preload = 'none',
      ...videoProps
    },
    ref
  ) => {
    // The SD does not provide videos in webm format,
    // which is an advantage since our videos neither loop nor restart correctly in this format on Safari.
    // Who's to blame? We don't know. Be cautious in case of a resolution change.
    const sources = useMemo(() => getVideoSources(file, ['sd', 'hd']), [file]);

    return (
      <video
        ref={ref}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        preload={preload}
        className={className}
        {...videoProps}
      >
        {sources.map((source, index) => (
          <source
            key={index}
            // Appending media query fixes the video not showing on iOS until played.
            // See : https://muffinman.io/blog/hack-for-ios-safari-to-display-html-video-thumbnail/
            src={source.src + '#t=0.001'}
            type={source.type}
          />
        ))}
      </video>
    );
  }
);

Video.displayName = 'Video';
